import {
  WSCard,
  WSElement,
  WSList,
  WSTable,
  WSTableCell,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InsuranceCoverage,
  InsuranceMonitor
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";

import { useGetNow } from "../../../../../shared/hooks/useGetNow";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../../../../MyResources/selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../../../../MyResources/selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import {
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../types";
import { ALL_REQUIREMENT_EVENTS } from "../../../utils";

import { useModalPartialMatchSubmitForReview } from "./ModalPartialMatchSubmitForReview";
import { useModalValidationDetails } from "./ModalValidationDetails";

type Props = {
  requirement: RequirementTableItemData;
  coverages: InsuranceCoverage[];
  coverageMonitor: InsuranceMonitor;
  callback: RequirementTableCallback;
};

export const CardPartialMatchSubmitForReview: React.FC<Props> = props => {
  const now = useGetNow();
  const modalValidationDetails = useModalValidationDetails();
  const modalSubmitForReview = useModalPartialMatchSubmitForReview();

  const tableData: WSTableItem<InsuranceCoverage>[] = props.coverages.map(
    coverage => ({
      data: coverage,
      id: coverage.id!
    })
  );

  const [selection, setSelection] = useState<WSTableItem<InsuranceCoverage>[]>(
    props.coverages.length === 1 ? [tableData[0]] : []
  );

  return (
    <WSCard
      actions={{
        alignment: "fill",
        buttons: [
          {
            disabled: selection.length === 0,
            label: "Submit for review",
            onClick: () => {
              modalSubmitForReview.open({
                insuranceCoverageId: selection[0].data.id!,
                insuranceCoverageMonitorId: props.coverageMonitor.id!,
                onSuccess: () => {
                  props.callback(
                    ALL_REQUIREMENT_EVENTS.SubmitCoverage,
                    props.requirement
                  );
                }
              });
            }
          }
        ]
      }}
      header={{
        label: {
          avatar: {
            colorBackground: "amber300",
            icon: "send-mail",
            type: "icon"
          },
          text: "Submit for review"
        }
      }}
    >
      <WSList gap="XL">
        <WSText.ParagraphSm color="gray500">
          Select a partially matched policy and submit it for client review. If
          the policy does not provide the right coverage or is rejected by the
          client, you will need to seek new coverage and upload the relevant
          documents.
        </WSText.ParagraphSm>

        <WSTable<InsuranceCoverage>
          columns={[
            {
              config: {
                header: {
                  text: "Policy"
                }
              },
              renderFunction: row => (
                <WSTableCell
                  secondaryText={`#${row.data.id}`}
                  text={row.data.policyNumber || row.data.carrier}
                />
              )
            },
            {
              config: {
                header: {
                  text: "Status"
                }
              },
              renderFunction: row => {
                return (
                  <WSTableCell
                    pill={selectorInsuranceCoverageStatusPillByExpirationDate(
                      row.data.expirationDate
                    )}
                    secondaryText={selectorInsuranceCoverageStatusDescriptionByInsurance(
                      row.data,
                      now
                    )}
                  />
                );
              }
            },
            {
              config: {
                header: {
                  text: "Result"
                }
              },
              renderFunction: row => (
                <WSElement>
                  <WSTableCell
                    pill={{
                      icon: true,
                      text: "Partial match",
                      theme: "warning"
                    }}
                  />
                  <WSText.ParagraphXs
                    color="blue400"
                    mt="XS"
                    onClick={() => {
                      modalValidationDetails.open({
                        coverageMonitor: props.coverageMonitor,
                        item: row.data
                      });
                    }}
                  >
                    View results
                  </WSText.ParagraphXs>
                </WSElement>
              )
            }
          ]}
          selection={selection}
          selectionType="radio"
          tableData={tableData}
          onSelectionChange={setSelection}
        />
      </WSList>
    </WSCard>
  );
};
